import {gql} from '@apollo/client';

export const GET_USER = gql`
  query GetUser {
    getUser {
      id
      email
      name
      photoUrl
      backgroundImageUrl
      disableAnimation
      clickSoundUrl
      checkSoundUrl
      dropdownSoundUrl
      switchSoundUrl
      slideSoundUrl
      soundVolume
      createdAt
      lastLogin
      notStudent
      groupIds
      inviteToGroupIds
      roles
      lastStreakDate
      currentStreak
      highestStreak
      selectedModules {
        moduleId
        topics
      }
    }
  }
`;

export const GET_QB_USER = GET_USER; // Keep for backward compatibility

export const LIST_GROUPS = gql`
  query ListGroups {
    listGroups {
      id
      name
      createdAt
      inviteeEmails
      users {
        id
        name
        photoUrl
      }
      owner {
        id
        name
        photoUrl
      }
    }
  }
`;

export const GET_GROUP = gql`
  query GetGroup($groupId: String!) {
    getGroup(groupId: $groupId) {
      id
      name
      createdAt
      inviteeEmails
      users {
        id
        name
        photoUrl
      }
      owner {
        id
        name
        photoUrl
      }
    }
  }
`;

export const LIST_PROFILES = gql`
  query ListProfiles($groupId: String!) {
    listProfiles(groupId: $groupId) {
      id
      name
      country
      examBoard
      yearGroup
      subjects
      ownerId
      stats {
        topic
        correctAnswers
        incorrectAnswers
      }
    }
  }
`;

export const GET_PROFILE = gql`
  query GetProfile($groupId: String!, $profileId: String!) {
    getProfile(groupId: $groupId, profileId: $profileId) {
      id
      name
      country
      examBoard
      yearGroup
      subjects
      ownerId
      stats {
        topic
        correctAnswers
        incorrectAnswers
      }
    }
  }
`;

export const LIST_EXAM_MODULES = gql`
  query ListExamModules {
    listModules {
      id
      name
      level
      examBoard
      subject
      topics {
        topic
        category
        detail
      }
      createdAt
      questionsPerTopic
      requiredRole
      attribution
      author
      source
      license
      disableStudy
      disableQuestion
    }
  }
`;

export const GET_EXAM_MODULE = gql`
  query GetExamModule($id: String!) {
    getModule(id: $id) {
      id
      name
      level
      examBoard
      subject
      topics {
        topic
        category
        detail
      }
      createdAt
      questionsPerTopic
      requiredRole
      attribution
      author
      source
      license
      disableStudy
      disableQuestion
    }
  }
`;

export const CREATE_EXAM_MODULE = gql`
  mutation CreateExamModule(
    $name: String!
    $level: SubjectLevel!
    $subject: StandardSubject!
    $topics: [ModuleTopicInput!]!
    $examBoard: String
    $questionsPerTopic: Int
    $requiredRole: String
    $attribution: String
    $author: String
    $source: String
    $license: String
    $disableStudy: Boolean
    $disableQuestion: Boolean
  ) {
    createModule(
      name: $name
      level: $level
      subject: $subject
      topics: $topics
      examBoard: $examBoard
      questionsPerTopic: $questionsPerTopic
      requiredRole: $requiredRole
      attribution: $attribution
      author: $author
      source: $source
      license: $license
      disableStudy: $disableStudy
      disableQuestion: $disableQuestion
    ) {
      id
      name
      level
      examBoard
      subject
      topics {
        topic
        category
        detail
      }
      createdAt
      questionsPerTopic
      requiredRole
      attribution
      author
      source
      license
      disableStudy
      disableQuestion
    }
  }
`;

export const UPDATE_EXAM_MODULE = gql`
  mutation UpdateExamModule(
    $id: String!
    $name: String
    $topics: [ModuleTopicInput!]
    $questionsPerTopic: Int
    $requiredRole: String
    $attribution: String
    $author: String
    $source: String
    $license: String
    $disableStudy: Boolean
    $disableQuestion: Boolean
  ) {
    updateModule(
      id: $id
      name: $name
      topics: $topics
      questionsPerTopic: $questionsPerTopic
      requiredRole: $requiredRole
      attribution: $attribution
      author: $author
      source: $source
      license: $license
      disableStudy: $disableStudy
      disableQuestion: $disableQuestion
    ) {
      id
      name
      level
      examBoard
      subject
      topics {
        topic
        category
        detail
      }
      createdAt
      questionsPerTopic
      requiredRole
      attribution
      author
      source
      license
      disableStudy
      disableQuestion
    }
  }
`;

export const DELETE_EXAM_MODULE = gql`
  mutation DeleteExamModule($id: String!) {
    deleteModule(id: $id)
  }
`;

export const GENERATE_QUESTIONS = gql`
  mutation GenerateQuestions($moduleId: String!, $topic: String!, $difficulty: Int!, $count: Int) {
    generateQuestions(moduleId: $moduleId, topic: $topic, difficulty: $difficulty, count: $count) {
      id
      questionText
      hint
      options
      answer
      explanation
      imageUrl
      graphJsonString
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
    }
  }
`;

export const GENERATE_QUESTION_PROMPTS = gql`
  query GenerateQuestionPrompts($moduleId: String!, $topic: String!, $difficulty: Int!) {
    generateQuestionPrompts(moduleId: $moduleId, topic: $topic, difficulty: $difficulty) {
      system
      user
    }
  }
`;

export const GENERATE_QUESTION_GRAPH_JSON_PROMPTS = gql`
  query GenerateQuestionGraphJsonPrompts(
    $questionText: String!
    $topic: String
    $difficulty: Int
    $hint: String
    $options: [String!]
    $answer: String
    $explanation: String
  ) {
    generateQuestionGraphJsonPrompts(
      questionText: $questionText
      topic: $topic
      difficulty: $difficulty
      hint: $hint
      options: $options
      answer: $answer
      explanation: $explanation
    ) {
      system
      user
    }
  }
`;

export const GENERATE_STUDY_PROMPTS = gql`
  query GenerateStudyPrompts($moduleId: String!, $topic: String!, $additionalInfo: String) {
    generateStudyPrompts(moduleId: $moduleId, topic: $topic, additionalInfo: $additionalInfo) {
      system
      user
    }
  }
`;

export const GENERATE_QUESTION_WITH_SEED_PROMPTS = gql`
  query GenerateQuestionWithSeedPrompts($moduleId: String!, $seedQuestionId: String!, $numberOfVariants: Int!) {
    generateQuestionWithSeedPrompts(
      moduleId: $moduleId
      seedQuestionId: $seedQuestionId
      numberOfVariants: $numberOfVariants
    ) {
      system
      user
    }
  }
`;

export const GENERATE_QUESTION_WITH_SEED = gql`
  mutation GenerateQuestionWithSeed(
    $moduleId: String!
    $seedQuestionId: String!
    $numberOfVariants: Int!
    $additionalInfo: String
  ) {
    generateQuestionWithSeed(
      moduleId: $moduleId
      seedQuestionId: $seedQuestionId
      numberOfVariants: $numberOfVariants
      additionalInfo: $additionalInfo
    )
  }
`;

export const LIST_SEED_QUESTIONS = gql`
  query ListSeedQuestions($moduleId: String!, $topic: String, $status: SeedQuestionStatus, $limit: Int, $offset: Int) {
    listSeedQuestions(moduleId: $moduleId, topic: $topic, status: $status, limit: $limit, offset: $offset) {
      id
      questionText
      topic
      options
      answer
      source
      status
      createdAt
    }
  }
`;

export const COUNT_SEED_QUESTIONS_BY_FILTERS = gql`
  query CountSeedQuestionsByFilters($moduleId: String!, $topics: [String!], $statuses: [SeedQuestionStatus!]) {
    countSeedQuestionsByFilters(moduleId: $moduleId, topics: $topics, statuses: $statuses)
  }
`;

export const CREATE_SEED_QUESTION = gql`
  mutation CreateSeedQuestion(
    $moduleId: String!
    $questionText: String!
    $topic: String!
    $options: [String!]!
    $answer: String!
    $source: SeedQuestionSource!
  ) {
    createSeedQuestion(
      moduleId: $moduleId
      questionText: $questionText
      topic: $topic
      options: $options
      answer: $answer
      source: $source
    ) {
      id
      questionText
      topic
      options
      answer
      source
      status
      createdAt
    }
  }
`;

export const UPDATE_SEED_QUESTION = gql`
  mutation UpdateSeedQuestion(
    $moduleId: String!
    $seedQuestionId: String!
    $questionText: String
    $topic: String
    $options: [String!]
    $answer: String
    $source: SeedQuestionSource
    $status: SeedQuestionStatus
  ) {
    updateSeedQuestion(
      moduleId: $moduleId
      seedQuestionId: $seedQuestionId
      questionText: $questionText
      topic: $topic
      options: $options
      answer: $answer
      source: $source
      status: $status
    ) {
      id
      questionText
      topic
      options
      answer
      source
      status
      createdAt
    }
  }
`;

export const GET_PDF_SYSTEM_PROMPT = gql`
  query GetPdfSystemPrompt($additionalInfo: String) {
    getPdfSystemPrompt(additionalInfo: $additionalInfo) {
      system
      user
    }
  }
`;

export const GET_PDF_EXTRACT_QUESTION_PROMPT = gql`
  query GetPdfExtractQuestionPrompt($additionalInfo: String) {
    getPdfExtractQuestionPrompt(additionalInfo: $additionalInfo) {
      system
      user
    }
  }
`;

export const GENERATE_QUESTIONS_BY_TOPIC = gql`
  mutation GenerateQuestionsByTopic($moduleId: String!, $topic: String!) {
    generateQuestionsByTopic(moduleId: $moduleId, topic: $topic) {
      id
      questionText
      hint
      options
      answer
      explanation
      imageUrl
      graphJsonString
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
    }
  }
`;

export const SHUFFLE_QUESTIONS = gql`
  mutation ShuffleQuestions($moduleId: String!) {
    shuffleQuestions(moduleId: $moduleId)
  }
`;

export const UPDATE_SELECTED_MODULES = gql`
  mutation UpdateSelectedModules($moduleId: String!, $topics: [String!]!) {
    updateSelectedModules(moduleId: $moduleId, topics: $topics) {
      id
      email
      name
      photoUrl
      backgroundImageUrl
      disableAnimation
      createdAt
      lastLogin
      notStudent
      groupIds
      inviteToGroupIds
      selectedModules {
        moduleId
        topics
      }
    }
  }
`;

export const REMOVE_SELECTED_MODULES = gql`
  mutation RemoveSelectedModules($moduleId: String!) {
    removeSelectedModules(moduleId: $moduleId) {
      id
      email
      name
      photoUrl
      backgroundImageUrl
      disableAnimation
      createdAt
      lastLogin
      notStudent
      groupIds
      inviteToGroupIds
      selectedModules {
        moduleId
        topics
      }
    }
  }
`;

export const ADD_SELECTED_MODULES = gql`
  mutation AddSelectedModules($moduleId: String!, $topics: [String!]!) {
    addSelectedModules(moduleId: $moduleId, topics: $topics) {
      id
      email
      name
      photoUrl
      backgroundImageUrl
      disableAnimation
      createdAt
      lastLogin
      notStudent
      groupIds
      inviteToGroupIds
      selectedModules {
        moduleId
        topics
      }
    }
  }
`;

export const CREATE_QUIZ_SESSION = gql`
  mutation CreateQuizSession(
    $moduleId: String!
    $topics: [String!]
    $difficulty: Int
    $count: Int
    $timedMode: QuizTimedMode
    $timeDurationSeconds: Int
  ) {
    createQuizSession(
      moduleId: $moduleId
      topics: $topics
      difficulty: $difficulty
      count: $count
      timedMode: $timedMode
      timeDurationSeconds: $timeDurationSeconds
    ) {
      id
      moduleId
      topics
      difficulty
      timedMode
      timeDurationSeconds
      questions {
        id
        questionText
        hint
        options
        answer
        explanation
        imageUrl
        graphJsonString
        moduleTopic {
          topic
          category
          detail
        }
        difficulty
        createdAt
        randomSeed
        status
        statusNote
        selectedAnswer
        isCorrect
      }
      score
      startedAt
      endedAt
    }
  }
`;

export const UPDATE_QUESTION_ANSWER = gql`
  mutation UpdateQuestionAnswer($sessionId: String!, $questionId: String!, $selectedAnswer: String!) {
    updateQuestionAnswer(sessionId: $sessionId, questionId: $questionId, selectedAnswer: $selectedAnswer) {
      id
      moduleId
      topics
      difficulty
      questions {
        id
        questionText
        hint
        options
        answer
        explanation
        imageUrl
        graphJsonString
        moduleTopic {
          topic
          category
          detail
        }
        difficulty
        createdAt
        randomSeed
        status
        statusNote
        selectedAnswer
        isCorrect
      }
      score
      startedAt
      endedAt
    }
  }
`;

export const END_QUIZ_SESSION = gql`
  mutation EndQuizSession($sessionId: String!) {
    endQuizSession(sessionId: $sessionId) {
      id
      moduleId
      topics
      difficulty
      questions {
        id
        questionText
        hint
        options
        answer
        explanation
        imageUrl
        graphJsonString
        moduleTopic {
          topic
          category
          detail
        }
        difficulty
        createdAt
        randomSeed
        status
        statusNote
        selectedAnswer
        isCorrect
      }
      score
      startedAt
      endedAt
    }
  }
`;

export const RETRY_QUIZ = gql`
  mutation RetryQuiz($moduleId: String!, $sessionId: String!) {
    retryQuiz(moduleId: $moduleId, sessionId: $sessionId) {
      id
      moduleId
      moduleName
      level
      examBoard
      subject
      topics
      difficulty
      timedMode
      timeDurationSeconds
      questions {
        id
        questionText
        hint
        options
        answer
        explanation
        imageUrl
        graphJsonString
        moduleTopic {
          topic
          category
          detail
        }
        difficulty
        createdAt
        randomSeed
        status
        statusNote
        selectedAnswer
        isCorrect
      }
      score
      startedAt
      endedAt
    }
  }
`;

export const LIST_USER_ACTIVE_SESSIONS = gql`
  query ListUserActiveSessions($limit: Int, $offset: Int) {
    listUserActiveSessions(limit: $limit, offset: $offset) {
      id
      moduleId
      moduleName
      level
      examBoard
      subject
      topics
      difficulty
      timedMode
      timeDurationSeconds
      questions {
        id
        questionText
        hint
        options
        answer
        explanation
        imageUrl
        graphJsonString
        moduleTopic {
          topic
          category
          detail
        }
        difficulty
        createdAt
        randomSeed
        status
        statusNote
        selectedAnswer
        isCorrect
      }
      score
      startedAt
      endedAt
    }
  }
`;

export const GET_USER_SESSION = gql`
  query GetUserSession($sessionId: String!) {
    getUserSession(sessionId: $sessionId) {
      id
      moduleId
      moduleName
      level
      examBoard
      subject
      topics
      difficulty
      timedMode
      timeDurationSeconds
      questions {
        id
        questionText
        hint
        options
        answer
        explanation
        imageUrl
        graphJsonString
        moduleTopic {
          topic
          category
          detail
        }
        difficulty
        createdAt
        randomSeed
        status
        statusNote
        selectedAnswer
        isCorrect
      }
      score
      startedAt
      endedAt
    }
  }
`;

export const LIST_USER_COMPLETED_SESSIONS = gql`
  query ListUserCompletedSessions($limit: Int, $offset: Int) {
    listUserCompletedSessions(limit: $limit, offset: $offset) {
      id
      moduleId
      moduleName
      level
      examBoard
      subject
      topics
      difficulty
      timedMode
      timeDurationSeconds
      questions {
        id
        questionText
        hint
        options
        answer
        explanation
        imageUrl
        graphJsonString
        moduleTopic {
          topic
          category
          detail
        }
        difficulty
        createdAt
        randomSeed
        status
        statusNote
        selectedAnswer
        isCorrect
      }
      score
      startedAt
      endedAt
    }
  }
`;

export const SET_AVATAR = gql`
  mutation SetAvatar($avatar: Base64ObjectInput) {
    setAvatar(avatar: $avatar) {
      id
      email
      name
      photoUrl
      backgroundImageUrl
      disableAnimation
      createdAt
      lastLogin
      notStudent
      groupIds
      inviteToGroupIds
      selectedModules {
        moduleId
        topics
      }
    }
  }
`;

export const SET_BACKGROUND_IMAGE = gql`
  mutation SetBackgroundImage($backgroundImage: Base64ObjectInput) {
    setBackgroundImage(backgroundImage: $backgroundImage) {
      id
      email
      name
      photoUrl
      backgroundImageUrl
      disableAnimation
      createdAt
      lastLogin
      notStudent
      groupIds
      inviteToGroupIds
      selectedModules {
        moduleId
        topics
      }
    }
  }
`;

export const SET_DISPLAY_NAME = gql`
  mutation SetDisplayName($name: String) {
    setDisplayName(name: $name) {
      id
      email
      name
      photoUrl
      backgroundImageUrl
      disableAnimation
      createdAt
      lastLogin
      notStudent
      groupIds
      inviteToGroupIds
      selectedModules {
        moduleId
        topics
      }
    }
  }
`;

export const SET_DISABLE_ANIMATION = gql`
  mutation SetDisableAnimation($disableAnimation: Boolean!) {
    setDisableAnimation(disableAnimation: $disableAnimation) {
      id
      email
      name
      photoUrl
      backgroundImageUrl
      disableAnimation
      createdAt
      lastLogin
      notStudent
      groupIds
      inviteToGroupIds
      selectedModules {
        moduleId
        topics
      }
    }
  }
`;

export const SET_SOUND_FILE = gql`
  mutation SetSoundFile($soundType: SoundType!, $soundFile: Base64ObjectInput) {
    setSoundFile(soundType: $soundType, soundFile: $soundFile) {
      id
      email
      name
      photoUrl
      backgroundImageUrl
      disableAnimation
      clickSoundUrl
      checkSoundUrl
      dropdownSoundUrl
      switchSoundUrl
      slideSoundUrl
      soundVolume
      createdAt
      lastLogin
      notStudent
      groupIds
      inviteToGroupIds
      selectedModules {
        moduleId
        topics
      }
    }
  }
`;

export const SET_SOUND_VOLUME = gql`
  mutation SetSoundVolume($soundVolume: Int!) {
    setSoundVolume(soundVolume: $soundVolume) {
      id
      email
      name
      photoUrl
      backgroundImageUrl
      disableAnimation
      clickSoundUrl
      checkSoundUrl
      dropdownSoundUrl
      switchSoundUrl
      slideSoundUrl
      soundVolume
      createdAt
      lastLogin
      notStudent
      groupIds
      inviteToGroupIds
      selectedModules {
        moduleId
        topics
      }
    }
  }
`;

export const ACCEPT_INVITE_TO_GROUP = gql`
  mutation AcceptInviteToGroup($groupId: String!) {
    acceptInviteToGroup(groupId: $groupId) {
      id
      email
      name
      photoUrl
      backgroundImageUrl
      disableAnimation
      createdAt
      lastLogin
      notStudent
      groupIds
      inviteToGroupIds
      selectedModules {
        moduleId
        topics
      }
    }
  }
`;

export const REJECT_INVITE_TO_GROUP = gql`
  mutation RejectInviteToGroup($groupId: String!) {
    rejectInviteToGroup(groupId: $groupId) {
      id
      email
      name
      photoUrl
      backgroundImageUrl
      disableAnimation
      createdAt
      lastLogin
      notStudent
      groupIds
      inviteToGroupIds
      selectedModules {
        moduleId
        topics
      }
    }
  }
`;

export const LEAVE_GROUP = gql`
  mutation LeaveGroup($groupId: String!) {
    leaveGroup(groupId: $groupId) {
      id
      email
      name
      photoUrl
      backgroundImageUrl
      disableAnimation
      createdAt
      lastLogin
      notStudent
      groupIds
      inviteToGroupIds
      selectedModules {
        moduleId
        topics
      }
    }
  }
`;

// Job-related queries and mutations
export const LIST_JOBS = gql`
  query ListJobs {
    listJobs {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const GET_JOB = gql`
  query GetJob($id: String!) {
    getJob(id: $id) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const GET_PENDING_JOBS = gql`
  query GetPendingJobs {
    getPendingJobs {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const GET_RUNNING_JOBS = gql`
  query GetRunningJobs {
    getRunningJobs {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const CREATE_QUESTION_GENERATION_JOB = gql`
  mutation CreateQuestionGenerationJob($moduleId: String!, $topics: [String!]!, $difficulties: [Int!], $count: Int) {
    createQuestionGenerationJob(moduleId: $moduleId, topics: $topics, difficulties: $difficulties, count: $count) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const CREATE_STUDY_GENERATION_JOB = gql`
  mutation CreateStudyGenerationJob($moduleId: String!, $topics: [String!]) {
    createStudyGenerationJob(moduleId: $moduleId, topics: $topics) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const CREATE_SEED_QUESTION_VARIANT_JOB = gql`
  mutation CreateSeedQuestionVariantJob(
    $moduleId: String!
    $seedQuestionCount: Int!
    $variantsPerSeed: Int
    $runLimit: Int
  ) {
    createSeedQuestionVariantJob(
      moduleId: $moduleId
      seedQuestionCount: $seedQuestionCount
      variantsPerSeed: $variantsPerSeed
      runLimit: $runLimit
    ) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const CREATE_QUESTION_SHUFFLE_JOB = gql`
  mutation CreateQuestionShuffleJob($moduleId: String!, $topics: [String!], $difficulties: [Int!]) {
    createQuestionShuffleJob(moduleId: $moduleId, topics: $topics, difficulties: $difficulties) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const LIST_QUESTIONS = gql`
  query ListQuestions(
    $moduleId: String!
    $topics: [String!]
    $difficulty: Int
    $status: QuestionStatus
    $limit: Int
    $offset: Int
  ) {
    listQuestions(
      moduleId: $moduleId
      topics: $topics
      difficulty: $difficulty
      status: $status
      limit: $limit
      offset: $offset
    ) {
      id
      questionText
      hint
      options
      answer
      explanation
      imageUrl
      graphJsonString
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
    }
  }
`;

export const UPDATE_JOB_STATUS = gql`
  mutation UpdateJobStatus($id: String!, $status: JobStatus!, $message: String, $progress: Int) {
    updateJobStatus(id: $id, status: $status, message: $message, progress: $progress) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const START_JOB = gql`
  mutation StartJob($id: String!) {
    startJob(id: $id) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const COMPLETE_JOB = gql`
  mutation CompleteJob($id: String!) {
    completeJob(id: $id) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const FAIL_JOB = gql`
  mutation FailJob($id: String!, $messages: [String!]!) {
    failJob(id: $id, messages: $messages) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const CANCEL_JOB = gql`
  mutation CancelJob($id: String!) {
    cancelJob(id: $id) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const UPDATE_JOB_PROGRESS = gql`
  mutation UpdateJobProgress($id: String!, $progress: Int!) {
    updateJobProgress(id: $id, progress: $progress) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const DELETE_JOB = gql`
  mutation DeleteJob($id: String!) {
    deleteJob(id: $id)
  }
`;

export const CLEANUP_OLD_JOBS = gql`
  mutation CleanupOldJobs($daysOld: Int) {
    cleanupOldJobs(daysOld: $daysOld)
  }
`;

export const RESUME_JOB = gql`
  mutation ResumeJob($id: String!) {
    resumeJob(id: $id) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const INCREASE_JOB_RUN_LIMIT = gql`
  mutation IncreaseJobRunLimit($id: String!, $amount: Int!) {
    increaseJobRunLimit(id: $id, amount: $amount) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const CREATE_QUESTION = gql`
  mutation CreateQuestion(
    $moduleId: String!
    $topic: String!
    $difficulty: Int!
    $questionText: String!
    $options: [String!]!
    $answer: String!
    $explanation: String!
    $hint: String
    $image: Base64ObjectInput
    $graphJsonString: String
    $seedQuestionId: String
  ) {
    createQuestion(
      moduleId: $moduleId
      topic: $topic
      difficulty: $difficulty
      questionText: $questionText
      options: $options
      answer: $answer
      explanation: $explanation
      hint: $hint
      image: $image
      graphJsonString: $graphJsonString
      seedQuestionId: $seedQuestionId
    ) {
      id
      questionText
      hint
      options
      answer
      explanation
      imageUrl
      graphJsonString
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
    }
  }
`;

export const CREATE_QUESTION_FROM_STRING = gql`
  mutation CreateQuestionFromString(
    $moduleId: String!
    $topic: String!
    $questionJsonString: String!
    $image: Base64ObjectInput
    $skipFormat: Boolean
    $seedQuestionId: String
  ) {
    createQuestionFromString(
      moduleId: $moduleId
      topic: $topic
      questionJsonString: $questionJsonString
      image: $image
      skipFormat: $skipFormat
      seedQuestionId: $seedQuestionId
    ) {
      id
      questionText
      hint
      options
      answer
      explanation
      imageUrl
      graphJsonString
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
    }
  }
`;

export const UPDATE_QUESTION = gql`
  mutation UpdateQuestion(
    $moduleId: String!
    $questionId: String!
    $topic: String
    $difficulty: Int
    $questionText: String
    $hint: String
    $options: [String!]
    $answer: String
    $explanation: String
    $status: QuestionStatus
    $statusNote: String
    $image: Base64ObjectInput
    $graphJsonString: String
    $seedQuestionId: String
  ) {
    updateQuestion(
      moduleId: $moduleId
      questionId: $questionId
      topic: $topic
      difficulty: $difficulty
      questionText: $questionText
      hint: $hint
      options: $options
      answer: $answer
      explanation: $explanation
      status: $status
      statusNote: $statusNote
      image: $image
      graphJsonString: $graphJsonString
      seedQuestionId: $seedQuestionId
    ) {
      id
      questionText
      hint
      options
      answer
      explanation
      imageUrl
      graphJsonString
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
    }
  }
`;

export const FLAG_QUESTION_AS_ERROR = gql`
  mutation FlagQuestionAsError($moduleId: String!, $questionId: String!, $statusNote: String) {
    flagQuestionAsError(moduleId: $moduleId, questionId: $questionId, statusNote: $statusNote) {
      id
      questionText
      hint
      options
      answer
      explanation
      imageUrl
      graphJsonString
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
    }
  }
`;

export const FLAG_QUESTION_AS_OUT_OF_SCOPE = gql`
  mutation FlagQuestionAsOutOfScope($moduleId: String!, $questionId: String!, $statusNote: String) {
    flagQuestionAsOutOfScope(moduleId: $moduleId, questionId: $questionId, statusNote: $statusNote) {
      id
      questionText
      hint
      options
      answer
      explanation
      imageUrl
      graphJsonString
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
    }
  }
`;

export const CHECK_QUESTION_WITH_RULES = gql`
  mutation CheckQuestionWithRules($moduleId: String!, $questionId: String!) {
    checkQuestionWithRules(moduleId: $moduleId, questionId: $questionId) {
      id
      questionText
      hint
      options
      answer
      explanation
      imageUrl
      graphJsonString
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
    }
  }
`;

export const CHECK_QUESTION_WITH_GPT = gql`
  mutation CheckQuestionWithGpt($moduleId: String!, $questionId: String!) {
    checkQuestionWithGpt(moduleId: $moduleId, questionId: $questionId) {
      id
      questionText
      hint
      options
      answer
      explanation
      imageUrl
      graphJsonString
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
    }
  }
`;

export const CHECK_QUESTION_WITH_RULES_DIRECT = gql`
  mutation CheckQuestionWithRulesDirect($questionInput: QuestionCheckInput!) {
    checkQuestionWithRulesDirect(questionInput: $questionInput) {
      id
      questionText
      hint
      options
      answer
      explanation
      imageUrl
      graphJsonString
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
    }
  }
`;

export const CHECK_QUESTION_WITH_GPT_DIRECT = gql`
  mutation CheckQuestionWithGptDirect($questionInput: QuestionCheckInput!, $audienceLevel: String!) {
    checkQuestionWithGptDirect(questionInput: $questionInput, audienceLevel: $audienceLevel) {
      id
      questionText
      hint
      options
      answer
      explanation
      imageUrl
      graphJsonString
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
    }
  }
`;

export const GENERATE_QUESTION_GRAPH_JSON = gql`
  mutation GenerateQuestionGraphJson(
    $questionText: String!
    $topic: String
    $difficulty: Int
    $hint: String
    $options: [String!]
    $answer: String
    $explanation: String
  ) {
    generateQuestionGraphJson(
      questionText: $questionText
      topic: $topic
      difficulty: $difficulty
      hint: $hint
      options: $options
      answer: $answer
      explanation: $explanation
    )
  }
`;

export const CREATE_QUESTION_VALIDATION_JOB = gql`
  mutation CreateQuestionValidationJob($moduleId: String!, $topics: [String!], $difficulties: [Int!]) {
    createQuestionValidationJob(moduleId: $moduleId, topics: $topics, difficulties: $difficulties) {
      id
      type
      status
      title
      description
      createdAt
      createdBy
    }
  }
`;

export const CREATE_BULK_CHANGE_QUESTION_STATUS_JOB = gql`
  mutation CreateBulkChangeQuestionStatusJob(
    $moduleId: String!
    $filterStatus: QuestionStatus!
    $changeStatus: QuestionStatus!
  ) {
    createBulkChangeQuestionStatusJob(moduleId: $moduleId, filterStatus: $filterStatus, changeStatus: $changeStatus) {
      id
      type
      status
      title
      description
      data
      messages
      progress
      createdBy
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`;

export const CREATE_QUESTION_VALIDATION_GPT_JOB = gql`
  mutation CreateQuestionValidationGptJob(
    $moduleId: String!
    $topics: [String!]
    $difficulties: [Int!]
    $runLimit: Int
  ) {
    createQuestionValidationGptJob(
      moduleId: $moduleId
      topics: $topics
      difficulties: $difficulties
      runLimit: $runLimit
    ) {
      id
      type
      status
      title
      description
      createdAt
      createdBy
    }
  }
`;

export const GET_MODULE_TOPICS_WITHOUT_STUDY = gql`
  query GetModuleTopicsWithoutStudy($moduleId: String!) {
    getModuleTopicsWithoutStudy(moduleId: $moduleId) {
      topic
      category
      detail
    }
  }
`;

export const GET_MODULE_TOPICS_WITH_STUDY = gql`
  query GetModuleTopicsWithStudy($moduleId: String!) {
    getModuleTopicsWithStudy(moduleId: $moduleId) {
      topic
      category
      detail
    }
  }
`;

// Progress-related queries and mutations
export const GET_USER_PROGRESS = gql`
  query GetUserProgress($examModuleId: String!) {
    getUserProgress(examModuleId: $examModuleId) {
      id
      lastSessionId
      lastUpdated
      scores {
        topic
        difficulty
        total
        correct
        accuracy
        adjustedAccuracy
        score
        historicData
        previousQuestionIds
      }
      study {
        topic
        done
        title
        progress
      }
      totalScore
      totalAnswered
      totalCorrect
      relatedScores
    }
  }
`;

export const LIST_USER_PROGRESS = gql`
  query ListUserProgress {
    listUserProgress {
      id
      lastSessionId
      lastUpdated
      scores {
        topic
        difficulty
        total
        correct
        accuracy
        adjustedAccuracy
        score
        historicData
        previousQuestionIds
      }
      study {
        topic
        done
        title
        progress
      }
      totalScore
      totalAnswered
      totalCorrect
      relatedScores
    }
  }
`;

export const UPDATE_PROGRESS_WITH_QUESTION = gql`
  mutation UpdateProgressWithQuestion(
    $examModuleId: String!
    $topic: String!
    $difficulty: Int!
    $isCorrect: Boolean!
    $sessionId: String!
  ) {
    updateProgressWithQuestion(
      examModuleId: $examModuleId
      topic: $topic
      difficulty: $difficulty
      isCorrect: $isCorrect
      sessionId: $sessionId
    ) {
      id
      lastSessionId
      lastUpdated
      scores {
        topic
        difficulty
        total
        correct
        accuracy
        adjustedAccuracy
        score
        historicData
        previousQuestionIds
      }
      study {
        topic
        done
        title
        progress
      }
      totalScore
      totalAnswered
      totalCorrect
      relatedScores
    }
  }
`;

export const DELETE_PROGRESS = gql`
  mutation DeleteProgress($examModuleId: String!) {
    deleteProgress(examModuleId: $examModuleId)
  }
`;

export const ADD_STUDY_PROGRESS = gql`
  mutation AddStudyProgress(
    $examModuleId: String!
    $topic: String!
    $done: Boolean
    $titles: [String!]
    $progress: Float
  ) {
    addStudyProgress(examModuleId: $examModuleId, topic: $topic, done: $done, titles: $titles, progress: $progress) {
      id
      lastSessionId
      lastUpdated
      scores {
        topic
        difficulty
        total
        correct
        accuracy
        adjustedAccuracy
        score
        historicData
        previousQuestionIds
      }
      study {
        topic
        done
        title
        progress
      }
      totalScore
      totalAnswered
      totalCorrect
      relatedScores
    }
  }
`;

export const COUNT_QUESTIONS_BY_FILTERS = gql`
  mutation CountQuestionsByFilters(
    $moduleId: String!
    $topics: [String!]
    $difficulties: [Int!]
    $statuses: [QuestionStatus!]
  ) {
    countQuestionsByFilters(moduleId: $moduleId, topics: $topics, difficulties: $difficulties, statuses: $statuses)
  }
`;

export const GET_SECRETS = gql`
  query GetSecrets {
    getSecrets {
      name
      maskedValue
    }
  }
`;

export const FETCH_QUESTION_FOR_REVIEW = gql`
  query FetchQuestionForReview(
    $moduleId: String!
    $topics: [String!]
    $difficulty: Int
    $status: QuestionStatus
    $skip: Int
  ) {
    fetchQuestionForReview(
      moduleId: $moduleId
      topics: $topics
      difficulty: $difficulty
      status: $status
      skip: $skip
    ) {
      id
      questionText
      hint
      options
      answer
      explanation
      imageUrl
      graphJsonString
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
      review {
        uid
        result
        timestamp
        note
      }
    }
  }
`;

export const CHANGE_QUESTION_STATUS = gql`
  mutation ChangeQuestionStatus(
    $moduleId: String!
    $questionId: String!
    $status: QuestionStatus!
    $statusNote: String
  ) {
    changeQuestionStatus(moduleId: $moduleId, questionId: $questionId, status: $status, statusNote: $statusNote) {
      id
      questionText
      hint
      options
      answer
      explanation
      seedQuestionId
      moduleTopic {
        topic
        category
        detail
      }
      difficulty
      createdAt
      randomSeed
      status
      statusNote
      review {
        uid
        result
        timestamp
        note
      }
    }
  }
`;

export const COUNT_QUESTIONS_BY_SEED_QUESTION = gql`
  query CountQuestionsBySeedQuestion($moduleId: String!, $seedQuestionId: String!) {
    countQuestionsBySeedQuestion(moduleId: $moduleId, seedQuestionId: $seedQuestionId)
  }
`;
